import { getRandomInt } from './randomUtils';

type Piece = null | 'player' | 'ai' | 'player-king' | 'ai-king';
type Board = Piece[][];

interface Move {
  fromRow: number;
  fromCol: number;
  toRow: number;
  toCol: number;
  captures?: { row: number; col: number; }[];
}

const BOARD_SIZE = 8;

// Enhanced evaluation weights
const WEIGHTS = {
  PIECE: 100,
  KING: 250,
  POSITION: 10,
  BACK_ROW: 50,
  CENTER_CONTROL: 15,
  PROTECTED: 20,
  MOBILITY: 8
};

const evaluatePosition = (board: Board): number => {
  let score = 0;
  
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const piece = board[row][col];
      if (!piece) continue;

      // Base piece values
      if (piece.startsWith('ai')) {
        score += piece.endsWith('king') ? WEIGHTS.KING : WEIGHTS.PIECE;
        
        // Position-based scoring
        score += (BOARD_SIZE - row) * WEIGHTS.POSITION; // Prefer forward positions
        
        // Center control bonus
        if ((row > 2 && row < 5) && (col > 2 && col < 5)) {
          score += WEIGHTS.CENTER_CONTROL;
        }

        // Back row protection
        if (row === BOARD_SIZE - 1) {
          score += WEIGHTS.BACK_ROW;
        }

        // Protected pieces (adjacent friendly pieces)
        if (isProtected(board, row, col, true)) {
          score += WEIGHTS.PROTECTED;
        }

        // Mobility score
        score += getMobilityScore(board, row, col, true) * WEIGHTS.MOBILITY;
      } else {
        score -= piece.endsWith('king') ? WEIGHTS.KING : WEIGHTS.PIECE;
        
        // Position-based scoring for opponent
        score -= row * WEIGHTS.POSITION;
        
        if ((row > 2 && row < 5) && (col > 2 && col < 5)) {
          score -= WEIGHTS.CENTER_CONTROL;
        }

        if (row === 0) {
          score -= WEIGHTS.BACK_ROW;
        }

        if (isProtected(board, row, col, false)) {
          score -= WEIGHTS.PROTECTED;
        }

        score -= getMobilityScore(board, row, col, false) * WEIGHTS.MOBILITY;
      }
    }
  }
  
  return score;
};

const isProtected = (board: Board, row: number, col: number, isAI: boolean): boolean => {
  const directions = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
  const prefix = isAI ? 'ai' : 'player';
  
  for (const [dr, dc] of directions) {
    const newRow = row + dr;
    const newCol = col + dc;
    
    if (newRow >= 0 && newRow < BOARD_SIZE && newCol >= 0 && newCol < BOARD_SIZE) {
      const piece = board[newRow][newCol];
      if (piece?.startsWith(prefix)) {
        return true;
      }
    }
  }
  
  return false;
};

const getMobilityScore = (board: Board, row: number, col: number, isAI: boolean): number => {
  const moves = getValidMoves(board, row, col);
  return moves.length;
};

const getValidMoves = (board: Board, row: number, col: number): Move[] => {
  const piece = board[row][col];
  if (!piece) return [];

  const moves: Move[] = [];
  const isKing = piece.endsWith('king');
  const isAI = piece.startsWith('ai');
  const directions = isKing ? [-1, 1] : isAI ? [1] : [-1];

  // Regular moves
  for (const dRow of directions) {
    for (const dCol of [-1, 1]) {
      const newRow = row + dRow;
      const newCol = col + dCol;
      
      if (isValidPosition(newRow, newCol) && !board[newRow][newCol]) {
        moves.push({
          fromRow: row,
          fromCol: col,
          toRow: newRow,
          toCol: newCol
        });
      }
    }
  }

  // Capture moves (these take priority)
  const captures = findCaptures(board, row, col, piece);
  moves.push(...captures);

  return moves;
};

const findCaptures = (board: Board, row: number, col: number, piece: Piece): Move[] => {
  const captures: Move[] = [];
  const isKing = piece?.endsWith('king');
  const isAI = piece?.startsWith('ai');
  const directions = isKing ? [-2, 2] : isAI ? [2] : [-2];

  for (const dRow of directions) {
    for (const dCol of [-2, 2]) {
      const newRow = row + dRow;
      const newCol = col + dCol;
      const jumpedRow = row + dRow/2;
      const jumpedCol = col + dCol/2;
      
      if (isValidPosition(newRow, newCol) && !board[newRow][newCol]) {
        const jumpedPiece = board[jumpedRow][jumpedCol];
        if (jumpedPiece && jumpedPiece.startsWith(isAI ? 'player' : 'ai')) {
          captures.push({
            fromRow: row,
            fromCol: col,
            toRow: newRow,
            toCol: newCol,
            captures: [{ row: jumpedRow, col: jumpedCol }]
          });

          // Look for multiple captures
          const newBoard = board.map(row => [...row]);
          newBoard[newRow][newCol] = piece;
          newBoard[row][col] = null;
          newBoard[jumpedRow][jumpedCol] = null;

          const additionalCaptures = findCaptures(newBoard, newRow, newCol, piece);
          for (const capture of additionalCaptures) {
            captures.push({
              fromRow: row,
              fromCol: col,
              toRow: capture.toRow,
              toCol: capture.toCol,
              captures: [
                { row: jumpedRow, col: jumpedCol },
                ...(capture.captures || [])
              ]
            });
          }
        }
      }
    }
  }
  
  return captures;
};

const isValidPosition = (row: number, col: number): boolean => {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE;
};

const minimax = (
  board: Board,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean,
  mustCapture: boolean = false
): number => {
  if (depth === 0) return evaluatePosition(board);

  const pieces = [];
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const piece = board[row][col];
      if (piece?.startsWith(isMaximizing ? 'ai' : 'player')) {
        pieces.push({ row, col });
      }
    }
  }

  if (pieces.length === 0) return isMaximizing ? -Infinity : Infinity;

  let bestScore = isMaximizing ? -Infinity : Infinity;
  let hasCaptures = false;

  // First check for captures
  for (const { row, col } of pieces) {
    const moves = getValidMoves(board, row, col);
    const captures = moves.filter(move => move.captures);
    
    if (captures.length > 0) hasCaptures = true;
    
    for (const move of (mustCapture ? captures : moves)) {
      const newBoard = makeMove(board, move);
      const score = minimax(
        newBoard,
        depth - 1,
        alpha,
        beta,
        !isMaximizing,
        move.captures ? true : false
      );

      if (isMaximizing) {
        bestScore = Math.max(bestScore, score);
        alpha = Math.max(alpha, score);
      } else {
        bestScore = Math.min(bestScore, score);
        beta = Math.min(beta, score);
      }

      if (beta <= alpha) break;
    }
  }

  // If we found captures, ignore non-capture moves
  if (hasCaptures && !mustCapture) {
    return minimax(board, depth, alpha, beta, isMaximizing, true);
  }

  return bestScore;
};

const makeMove = (board: Board, move: Move): Board => {
  const newBoard = board.map(row => [...row]);
  const piece = newBoard[move.fromRow][move.fromCol];
  
  // Move the piece
  newBoard[move.toRow][move.toCol] = piece;
  newBoard[move.fromRow][move.fromCol] = null;
  
  // Handle captures
  if (move.captures) {
    move.captures.forEach(capture => {
      newBoard[capture.row][capture.col] = null;
    });
  }
  
  // King promotion
  if (piece === 'ai' && move.toRow === BOARD_SIZE - 1) {
    newBoard[move.toRow][move.toCol] = 'ai-king';
  } else if (piece === 'player' && move.toRow === 0) {
    newBoard[move.toRow][move.toCol] = 'player-king';
  }
  
  return newBoard;
};

export const findBestMove = (board: Board, difficulty: string): Move => {
  const depthMap = {
    'easy': 2,
    'medium': 4,
    'hard': 6
  };
  
  const depth = depthMap[difficulty as keyof typeof depthMap] || 4;
  let bestScore = -Infinity;
  let bestMove: Move | null = null;
  
  // First check for any available captures
  let hasCaptures = false;
  const allMoves: Move[] = [];
  
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      const piece = board[row][col];
      if (piece?.startsWith('ai')) {
        const moves = getValidMoves(board, row, col);
        allMoves.push(...moves);
        if (moves.some(move => move.captures)) {
          hasCaptures = true;
        }
      }
    }
  }

  // If there are captures available, only consider capture moves
  const movesToConsider = hasCaptures 
    ? allMoves.filter(move => move.captures)
    : allMoves;

  // Randomize move order for variety in easy mode
  if (difficulty === 'easy') {
    for (let i = movesToConsider.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [movesToConsider[i], movesToConsider[j]] = [movesToConsider[j], movesToConsider[i]];
    }
  }

  for (const move of movesToConsider) {
    const newBoard = makeMove(board, move);
    const score = minimax(newBoard, depth - 1, -Infinity, Infinity, false);
    
    // Add randomness for easy mode
    const finalScore = difficulty === 'easy'
      ? score + getRandomInt(-200, 200)
      : score;
    
    if (finalScore > bestScore) {
      bestScore = finalScore;
      bestMove = move;
    }
  }
  
  return bestMove || movesToConsider[0];
};